/**
 * Ambient backyard rabbit — shared behavior for every yard that opts in.
 *
 * Deliberately outside the Friends / playable-character system: the rabbit is
 * scenery that happens to react. It has no dialogue, no quest, no inventory,
 * and it never blocks the player.
 *
 * This module is pure. It owns timing and position; the view owns pixels.
 * Randomness arrives through `rng` so tests can pin every decision.
 */

import type { SpawnPoint, WalkObstacle } from '@/world-prototype/types';
import { creatureDistance } from '@/world-prototype/creatures/creatureGeometry';
import {
  boundsFromPolygon,
  pointInAnyObstacle,
  pointInPolygon,
} from '@/world-prototype/movement/walkArea';
import {
  isNearPoint,
  WILDLIFE_SLEEP_TIMING,
} from '@/world-prototype/creatures/wildlifeSleep';

/** Art poses sliced from Rabbit-Poses-1.png, all facing right. */
export type RabbitPose = 'sit' | 'alert' | 'hop' | 'sniff' | 'dash' | 'sleep';

export type RabbitPhase =
  /**
   * Not on stage yet. Only ever true before the first appearance of a session —
   * once the rabbit is in the yard it stays there.
   */
  | 'away'
  /** Hopping in from a cover point. */
  | 'arriving'
  /** Sitting / sniffing on the grass. */
  | 'settled'
  /** Mid-hop between two grass spots. */
  | 'hopping'
  /** Ears up, deciding to bolt. */
  | 'startled'
  /** Running back to a cover point to rest; stays visible on arrival. */
  | 'leaving'
  /** Hopping toward tossed garden food. */
  | 'seekingFood'
  /** Nibbling at garden food on the grass. */
  | 'eatingFood'
  /** Heading to the nighttime tuck-in spot. */
  | 'goToSleep'
  /** Curled up until morning. */
  | 'sleeping'
  /** Brief wake beat before resuming ambient roaming. */
  | 'waking';

export type RabbitFacing = 'left' | 'right';

/** An edge / cover spot the rabbit enters from and escapes to. */
export interface RabbitCoverPoint {
  id: string;
  x: number;
  y: number;
}

/** Per-yard geography. Behavior and timing stay shared across yards. */
export interface RabbitYardConfig {
  yardId: string;
  /**
   * Grass the rabbit may stand on (world coords 0–1). Authored inside the
   * fence and above the patio so hops never cross scenery.
   */
  roamPolygon: readonly SpawnPoint[];
  /** Believable entry / exit spots: fence corners, tree bases, bushes. */
  cover: readonly RabbitCoverPoint[];
}

/** Shared timing. Ranges are inclusive-low, exclusive-high milliseconds. */
export const RABBIT_TIMING = {
  /** Yard has to feel empty first, so the rabbit is a surprise. */
  firstAppearMs: [900, 1_600],
  /** Before the very first appearance of a session only. */
  reappearMs: [3_000, 5_000],
  /** A long sit tucked against cover between wandering spells. */
  restMs: [6_000, 12_000],
  /** How long a visit lasts before it retreats to cover to rest. */
  visitMs: [38_000, 72_000],
  /** Pause between hops. */
  settleMs: [1_400, 3_400],
  /** Ears-up beat before bolting, so kids see the reaction. */
  startleMs: [420, 780],
  /** Ears-up beat when it merely noticed the player — long enough to walk over. */
  noticeMs: [1_800, 2_600],
  /** One hop, and one flee hop. */
  hopMs: [520, 700],
  fleeHopMs: [380, 480],
} as const;

/** Hop reach in world-width units. */
export const RABBIT_HOP_DISTANCE = { min: 0.045, max: 0.105 } as const;
/** Flee hops cover more ground. */
export const RABBIT_FLEE_DISTANCE = { min: 0.1, max: 0.17 } as const;

/**
 * Forgiving comfort radius (world-width units). Generous enough that a child
 * gets to walk up and look, tight enough that the reaction feels caused.
 */
export const RABBIT_COMFORT_RADIUS = 0.085;

/**
 * Inside this radius a tap spooks the rabbit; outside it, the tap only makes it
 * look up while the player walks over.
 */
export const RABBIT_NOTICE_RADIUS = 0.2;

/** The rabbit refuses to hop into the player's feet. */
export const RABBIT_PLAYER_CLEARANCE = 0.05;

/** Spooks tolerated in one visit before it gives up and leaves. */
export const RABBIT_SPOOKS_BEFORE_LEAVING = 3;

/**
 * Travel speed for the run to cover, in world-width units per second. Exit
 * points can be most of a yard away, so the leaving hop is timed from the
 * actual distance instead of a fixed beat — otherwise a long exit reads as the
 * rabbit being fired out of a cannon.
 */
export const RABBIT_LEAVE_SPEED = 0.26;
/** Bounds on the exit run so it is neither a twitch nor a slow march. */
export const RABBIT_LEAVE_MS = { min: 620, max: 2_400 } as const;

/** Peak visual lift of a hop, as a fraction of the rabbit's rendered height. */
export const RABBIT_HOP_LIFT = 0.34;

interface RabbitHop {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  elapsedMs: number;
  durationMs: number;
  /** 0 for a walk-in, 1 for a full arcing hop. */
  lift: number;
}

export interface RabbitState {
  phase: RabbitPhase;
  pose: RabbitPose;
  x: number;
  y: number;
  facing: RabbitFacing;
  /** Milliseconds left in the current phase. */
  timerMs: number;
  hop: RabbitHop | null;
  /** Vertical hop arc, 0…1 of `RABBIT_HOP_LIFT`. */
  liftT: number;
  /** Player-caused frights so far this visit. */
  spooks: number;
  /** Milliseconds left before it heads for cover unprompted. */
  visitMs: number;
  /** A poke landed and the startle beat is running. */
  fleeQueued: boolean;
  /**
   * Loose destination somewhere else in the yard. Hops lean toward it, so the
   * rabbit actually tours the lawn instead of random-walking a small patch
   * around wherever it came in.
   */
  goal: SpawnPoint | null;
  /**
   * Milliseconds until the rabbit pursues landed garden food.
   * `-1` = not tracking; `0` = pursue this frame after countdown.
   */
  foodNoticeMs: number;
  /** Nighttime tuck-in destination. */
  sleepTarget: SpawnPoint | null;
}

/** Pause after food lands before the rabbit reacts (ms). */
export const RABBIT_FOOD_NOTICE_MS = [700, 1_200] as const;

/** Nibble beat at the food (ms). */
export const RABBIT_FOOD_EAT_MS = [1_400, 2_100] as const;

export interface GardenFoodTarget {
  x: number;
  y: number;
  /** Food is on the grass and not yet being eaten. */
  available: boolean;
  onTargeted?: () => void;
  onBeginEat?: () => void;
  onEatComplete?: () => void;
}

/** How close counts as having reached tossed garden food. */
const RABBIT_FOOD_REACHED = 0.045;

/** How close counts as having reached the wander goal. */
const RABBIT_GOAL_REACHED = 0.09;
/** Share of hops that lean toward the goal; the rest stay aimless. */
const RABBIT_GOAL_BIAS = 0.75;
/** Random spread around the goal heading, in radians. */
const RABBIT_GOAL_SPREAD = 1.1;

export interface RabbitStepContext {
  config: RabbitYardConfig;
  /** Player ground position, or null when there is nobody to react to. */
  player: SpawnPoint | null;
  /** World width / height, for aspect-correct distances. */
  worldAspect: number;
  /** Prop footprints to stay out of. */
  obstacles?: readonly WalkObstacle[];
  /** Landed garden food for this yard, if any. */
  gardenFood?: GardenFoodTarget | null;
  rng?: () => number;
}

export function isRabbitSleepPhase(phase: RabbitPhase): boolean {
  return phase === 'goToSleep' || phase === 'sleeping' || phase === 'waking';
}

/** Night falls — cancel daytime behavior and head for the tuck-in spot. */
export function requestRabbitSleep(
  state: RabbitState,
  target: SpawnPoint,
  worldAspect: number,
  rng: () => number = Math.random,
): void {
  state.foodNoticeMs = -1;
  state.fleeQueued = false;
  state.hop = null;
  state.liftT = 0;
  state.sleepTarget = target;

  if (state.phase === 'away') {
    state.x = target.x;
    state.y = target.y;
    enterRabbitSleep(state);
    return;
  }

  if (isNearSleepSpot(state, target, worldAspect)) {
    state.x = target.x;
    state.y = target.y;
    enterRabbitSleep(state);
    return;
  }

  state.phase = 'goToSleep';
  state.pose = 'hop';
  beginHop(state, target, pickRange(RABBIT_TIMING.hopMs, rng), 0.45);
}

/** Dawn — brief wake beat, then resume ambient roaming from the sleep spot. */
export function requestRabbitWake(
  state: RabbitState,
  rng: () => number = Math.random,
): void {
  if (!isRabbitSleepPhase(state.phase)) return;
  state.phase = 'waking';
  state.pose = 'sit';
  state.hop = null;
  state.liftT = 0;
  state.sleepTarget = null;
  state.timerMs = WILDLIFE_SLEEP_TIMING.wakePauseMs;
  state.visitMs = pickRange(RABBIT_TIMING.visitMs, rng);
  state.goal = null;
}

function isNearSleepSpot(
  state: RabbitState,
  target: SpawnPoint,
  worldAspect: number,
): boolean {
  return isNearPoint(state, target, worldAspect);
}

function enterRabbitSleep(state: RabbitState): void {
  state.phase = 'sleeping';
  state.pose = 'sleep';
  state.hop = null;
  state.liftT = 0;
  state.fleeQueued = false;
  state.timerMs = 0;
}

function stepRabbitSleep(
  state: RabbitState,
  dt: number,
  ctx: RabbitStepContext,
  rng: () => number,
): void {
  if (state.phase === 'sleeping') return;

  if (state.phase === 'waking') {
    state.timerMs -= dt;
    if (state.timerMs <= 0) {
      state.phase = 'settled';
      state.pose = rng() < 0.45 ? 'sniff' : 'sit';
      state.timerMs = pickRange(RABBIT_TIMING.settleMs, rng);
      state.goal = pickGoal(state, ctx, rng);
    }
    return;
  }

  if (state.hop) {
    advanceHop(state, dt);
    if (state.hop) return;
    if (isNearSleepSpot(state, state.sleepTarget!, ctx.worldAspect)) {
      enterRabbitSleep(state);
      return;
    }
    state.pose = 'hop';
    beginHop(
      state,
      state.sleepTarget!,
      pickRange(RABBIT_TIMING.hopMs, rng),
      0.45,
    );
    return;
  }

  if (state.sleepTarget) {
    state.pose = 'hop';
    beginHop(
      state,
      state.sleepTarget,
      pickRange(RABBIT_TIMING.hopMs, rng),
      0.45,
    );
  }
}


export function isRabbitVisible(state: RabbitState): boolean {
  return state.phase !== 'away';
}

/** Fresh state: off-stage, waiting out the first appearance delay. */
export function createRabbitState(rng: () => number = Math.random): RabbitState {
  return {
    phase: 'away',
    pose: 'sit',
    x: 0,
    y: 0,
    facing: 'right',
    timerMs: pickRange(RABBIT_TIMING.firstAppearMs, rng),
    hop: null,
    liftT: 0,
    spooks: 0,
    visitMs: 0,
    fleeQueued: false,
    goal: null,
    foodNoticeMs: -1,
    sleepTarget: null,
  };
}

/** Aspect-correct distance in world-width units. */
export function rabbitDistance(
  a: SpawnPoint,
  b: SpawnPoint,
  worldAspect: number,
): number {
  return creatureDistance(a, b, worldAspect);
}

/**
 * Tap / click from close range, or the player walking into the comfort radius.
 * Ears up, then it bolts. Ignored unless the rabbit is calmly on the grass, so
 * a flurry of taps cannot stack reactions.
 */
export function pokeRabbit(
  state: RabbitState,
  rng: () => number = Math.random,
): void {
  if (isRabbitSleepPhase(state.phase)) return;
  if (state.phase !== 'settled' && state.phase !== 'hopping') return;
  startStartle(state, rng, true);
}

/**
 * Tap / click from across the yard. The rabbit looks up and holds still rather
 * than fleeing, so walking over is worth doing instead of pointless.
 */
export function noticeRabbit(
  state: RabbitState,
  rng: () => number = Math.random,
): void {
  if (isRabbitSleepPhase(state.phase)) return;
  if (state.phase !== 'settled' && state.phase !== 'hopping') return;
  startStartle(state, rng, false);
}

function startStartle(
  state: RabbitState,
  rng: () => number,
  flee: boolean,
): void {
  state.phase = 'startled';
  state.pose = 'alert';
  state.hop = null;
  state.liftT = 0;
  state.fleeQueued = flee;
  state.timerMs = pickRange(
    flee ? RABBIT_TIMING.startleMs : RABBIT_TIMING.noticeMs,
    rng,
  );
  if (flee) state.spooks += 1;
  state.foodNoticeMs = -1;
}

/**
 * Advance the rabbit by `dtMs`. Returns the same object, mutated — the view
 * holds one long-lived state per yard.
 */
export function stepRabbit(
  state: RabbitState,
  dtMs: number,
  ctx: RabbitStepContext,
): RabbitState {
  const rng = ctx.rng ?? Math.random;
  const dt = Math.max(0, Math.min(dtMs, 120));

  if (isRabbitSleepPhase(state.phase)) {
    stepRabbitSleep(state, dt, ctx, rng);
    return state;
  }

  if (stepGardenFood(state, dt, ctx, rng)) {
    return state;
  }

  if (state.phase === 'away') {
    state.timerMs -= dt;
    if (state.timerMs <= 0) enterYard(state, ctx, rng);
    return state;
  }

  state.visitMs -= dt;

  // Proximity fright outranks a calm pause. Deliberately excludes 'hopping':
  // re-startling mid-hop would cancel the escape every frame the player stayed
  // close, leaving the rabbit vibrating in place instead of getting away.
  if (
    state.phase === 'settled' &&
    ctx.player &&
    rabbitDistance(state, ctx.player, ctx.worldAspect) < RABBIT_COMFORT_RADIUS
  ) {
    startStartle(state, rng, true);
    return state;
  }

  if (state.hop) {
    advanceHop(state, dt);
    if (state.hop) return state;
    // Hop finished — fall through so the landing decision happens this frame.
    if (state.phase === 'leaving') {
      restAtCover(state, ctx, rng);
      return state;
    }
    if (state.phase === 'arriving' || state.phase === 'hopping') {
      settle(state, ctx, rng);
      return state;
    }
  }

  state.timerMs -= dt;
  if (state.timerMs > 0) return state;

  switch (state.phase) {
    case 'startled':
      if (!state.fleeQueued) {
        // It only looked up. Go back to grazing.
        settle(state, ctx, rng);
      } else if (state.spooks >= RABBIT_SPOOKS_BEFORE_LEAVING) {
        startLeaving(state, ctx, rng);
      } else {
        startFleeHop(state, ctx, rng);
      }
      return state;
    case 'settled':
      if (state.visitMs <= 0) startLeaving(state, ctx, rng);
      else startRoamHop(state, ctx, rng);
      return state;
    default:
      return state;
  }
}

function enterYard(
  state: RabbitState,
  ctx: RabbitStepContext,
  rng: () => number,
): void {
  const entry = pickCover(ctx.config, rng);
  if (!entry) {
    // Misconfigured yard: stay away rather than pop into the middle of it.
    state.timerMs = pickRange(RABBIT_TIMING.reappearMs, rng);
    return;
  }
  state.x = entry.x;
  state.y = entry.y;
  state.spooks = 0;
  state.fleeQueued = false;
  state.visitMs = pickRange(RABBIT_TIMING.visitMs, rng);
  state.goal = pickGoal(state, ctx, rng);
  state.phase = 'arriving';
  state.pose = 'hop';

  const target =
    pickRoamTarget(state, ctx, rng, RABBIT_HOP_DISTANCE) ?? {
      x: entry.x,
      y: entry.y,
    };
  beginHop(state, target, pickRange(RABBIT_TIMING.hopMs, rng), 1);
}

/**
 * End of a visit. The rabbit tucks up against its cover and rests there rather
 * than vanishing: a creature that blinks out of the yard and pops back reads as
 * a bug to a child, and there is no cover in these plates solid enough to hide
 * behind convincingly. After the rest it simply starts touring again.
 */
function restAtCover(
  state: RabbitState,
  ctx: RabbitStepContext,
  rng: () => number,
): void {
  state.phase = 'settled';
  state.pose = 'sit';
  state.hop = null;
  state.liftT = 0;
  state.spooks = 0;
  state.fleeQueued = false;
  state.visitMs = pickRange(RABBIT_TIMING.visitMs, rng);
  state.goal = pickGoal(state, ctx, rng);
  state.timerMs = pickRange(RABBIT_TIMING.restMs, rng);
}

function settle(
  state: RabbitState,
  ctx: RabbitStepContext,
  rng: () => number,
): void {
  if (
    !state.goal ||
    rabbitDistance(state, state.goal, ctx.worldAspect) < RABBIT_GOAL_REACHED
  ) {
    state.goal = pickGoal(state, ctx, rng);
  }
  state.phase = 'settled';
  state.hop = null;
  state.liftT = 0;
  state.fleeQueued = false;
  // A nose-down sniff reads as curiosity; upright sitting reads as watching.
  state.pose = rng() < 0.45 ? 'sniff' : 'sit';
  state.timerMs = pickRange(RABBIT_TIMING.settleMs, rng);
}

function startRoamHop(
  state: RabbitState,
  ctx: RabbitStepContext,
  rng: () => number,
): void {
  const target = pickRoamTarget(state, ctx, rng, RABBIT_HOP_DISTANCE);
  if (!target) {
    state.timerMs = pickRange(RABBIT_TIMING.settleMs, rng);
    return;
  }
  state.phase = 'hopping';
  state.pose = 'hop';
  beginHop(state, target, pickRange(RABBIT_TIMING.hopMs, rng), 1);
}

/** Bolt away from the player, staying on legal grass. */
function startFleeHop(
  state: RabbitState,
  ctx: RabbitStepContext,
  rng: () => number,
): void {
  const away = fleeTarget(state, ctx, rng);
  if (!away) {
    startLeaving(state, ctx, rng);
    return;
  }
  state.phase = 'hopping';
  state.pose = 'dash';
  state.fleeQueued = false;
  beginHop(state, away, pickRange(RABBIT_TIMING.fleeHopMs, rng), 1);
}

function startLeaving(
  state: RabbitState,
  ctx: RabbitStepContext,
  rng: () => number,
): void {
  const exit = pickExitCover(state, ctx, rng);
  if (!exit) {
    // Nowhere to retreat to: rest where it stands rather than blink out.
    restAtCover(state, ctx, rng);
    return;
  }
  state.phase = 'leaving';
  state.pose = 'dash';
  state.fleeQueued = false;
  const travel = rabbitDistance(state, exit, ctx.worldAspect);
  const durationMs = Math.min(
    RABBIT_LEAVE_MS.max,
    Math.max(RABBIT_LEAVE_MS.min, (travel / RABBIT_LEAVE_SPEED) * 1_000),
  );
  // Low arc: a bunny running for the fence stays close to the grass.
  beginHop(state, exit, durationMs, 0.35);
}

function beginHop(
  state: RabbitState,
  target: SpawnPoint,
  durationMs: number,
  lift: number,
): void {
  state.hop = {
    fromX: state.x,
    fromY: state.y,
    toX: target.x,
    toY: target.y,
    elapsedMs: 0,
    durationMs: Math.max(1, durationMs),
    lift,
  };
  if (Math.abs(target.x - state.x) > 0.002) {
    state.facing = target.x < state.x ? 'left' : 'right';
  }
}

function advanceHop(state: RabbitState, dt: number): void {
  const hop = state.hop;
  if (!hop) return;
  hop.elapsedMs += dt;
  const t = Math.min(1, hop.elapsedMs / hop.durationMs);
  state.x = hop.fromX + (hop.toX - hop.fromX) * t;
  state.y = hop.fromY + (hop.toY - hop.fromY) * t;
  // Parabola: feet leave the grass and land back on it.
  state.liftT = hop.lift * 4 * t * (1 - t);
  if (t >= 1) {
    state.x = hop.toX;
    state.y = hop.toY;
    state.liftT = 0;
    state.hop = null;
  }
}

function pickCover(
  config: RabbitYardConfig,
  rng: () => number,
): RabbitCoverPoint | null {
  if (config.cover.length === 0) return null;
  return config.cover[Math.floor(rng() * config.cover.length) % config.cover.length];
}

/**
 * Where to run when the visit is over: the nearest cover the rabbit can reach
 * without running past the player. Nearest rather than farthest, because a real
 * rabbit ducks into whatever is closest — and because sprinting the length of
 * the yard looks like a glitch rather than an exit.
 */
function pickExitCover(
  state: RabbitState,
  ctx: RabbitStepContext,
  rng: () => number,
): RabbitCoverPoint | null {
  const { cover } = ctx.config;
  if (cover.length === 0) return null;
  if (!ctx.player) return nearestCover(state, cover, ctx.worldAspect);

  const safe = cover.filter(
    (point) =>
      rabbitDistance(point, ctx.player!, ctx.worldAspect) >
      RABBIT_COMFORT_RADIUS,
  );
  const pool = safe.length > 0 ? safe : cover;
  return nearestCover(state, pool, ctx.worldAspect) ?? pickCover(ctx.config, rng);
}

function nearestCover(
  from: SpawnPoint,
  cover: readonly RabbitCoverPoint[],
  worldAspect: number,
): RabbitCoverPoint | null {
  let best: RabbitCoverPoint | null = null;
  let bestDist = Infinity;
  for (const point of cover) {
    const dist = rabbitDistance(from, point, worldAspect);
    if (dist < bestDist) {
      bestDist = dist;
      best = point;
    }
  }
  return best;
}

function isLegalSpot(
  point: SpawnPoint,
  ctx: RabbitStepContext,
  playerClearance: number,
): boolean {
  if (!pointInPolygon(point.x, point.y, ctx.config.roamPolygon)) return false;
  if (
    ctx.obstacles &&
    ctx.obstacles.length > 0 &&
    pointInAnyObstacle(point.x, point.y, ctx.obstacles)
  ) {
    return false;
  }
  if (
    ctx.player &&
    rabbitDistance(point, ctx.player, ctx.worldAspect) < playerClearance
  ) {
    return false;
  }
  return true;
}

/**
 * A far-off legal spot to drift toward. Sampled from the roam polygon's box and
 * rejected until it lands on real grass well away from where the rabbit already
 * is, so each goal is worth a few hops rather than one.
 */
function pickGoal(
  state: RabbitState,
  ctx: RabbitStepContext,
  rng: () => number,
): SpawnPoint | null {
  const bounds = boundsFromPolygon(ctx.config.roamPolygon);
  let fallback: SpawnPoint | null = null;
  for (let attempt = 0; attempt < 24; attempt += 1) {
    const candidate = {
      x: bounds.xMin + rng() * (bounds.xMax - bounds.xMin),
      y: bounds.yMin + rng() * (bounds.yMax - bounds.yMin),
    };
    // Zero clearance: the goal is a heading, not a landing spot, so it may sit
    // near the player. Individual hops still respect the clearance.
    if (!isLegalSpot(candidate, ctx, 0)) continue;
    fallback ??= candidate;
    if (rabbitDistance(state, candidate, ctx.worldAspect) > 0.2) return candidate;
  }
  return fallback;
}

/** Random legal grass spot within hop reach, or null after a few misses. */
function pickRoamTarget(
  state: RabbitState,
  ctx: RabbitStepContext,
  rng: () => number,
  reach: { min: number; max: number },
): SpawnPoint | null {
  const aspect = ctx.worldAspect > 0 ? ctx.worldAspect : 1;
  const goalAngle = state.goal
    ? Math.atan2((state.goal.y - state.y) / aspect, state.goal.x - state.x)
    : null;
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const angle =
      goalAngle !== null && rng() < RABBIT_GOAL_BIAS
        ? goalAngle + (rng() - 0.5) * RABBIT_GOAL_SPREAD
        : rng() * Math.PI * 2;
    const dist = reach.min + rng() * (reach.max - reach.min);
    const candidate = {
      x: state.x + Math.cos(angle) * dist,
      y: state.y + Math.sin(angle) * dist * aspect,
    };
    if (isLegalSpot(candidate, ctx, RABBIT_PLAYER_CLEARANCE)) return candidate;
  }
  return null;
}

/** Legal grass spot directly away from the player. */
function fleeTarget(
  state: RabbitState,
  ctx: RabbitStepContext,
  rng: () => number,
): SpawnPoint | null {
  const aspect = ctx.worldAspect > 0 ? ctx.worldAspect : 1;
  const player = ctx.player;
  const baseAngle = player
    ? Math.atan2((state.y - player.y) / aspect, state.x - player.x)
    : rng() * Math.PI * 2;

  // Straight away first, then progressively wider fans so a rabbit cornered
  // against the fence still finds an out.
  const spreads = [0, 0.35, -0.35, 0.75, -0.75, 1.2, -1.2];
  for (const spread of spreads) {
    const angle = baseAngle + spread;
    for (const scale of [1, 0.7, 0.45]) {
      const dist =
        (RABBIT_FLEE_DISTANCE.min +
          rng() * (RABBIT_FLEE_DISTANCE.max - RABBIT_FLEE_DISTANCE.min)) *
        scale;
      const candidate = {
        x: state.x + Math.cos(angle) * dist,
        y: state.y + Math.sin(angle) * dist * aspect,
      };
      if (isLegalSpot(candidate, ctx, RABBIT_PLAYER_CLEARANCE)) return candidate;
    }
  }
  return null;
}

function pickRange(range: readonly [number, number], rng: () => number): number {
  return range[0] + rng() * (range[1] - range[0]);
}

/**
 * Garden food pursuit — returns true when seeking/eating food owns this frame.
 */
function stepGardenFood(
  state: RabbitState,
  dt: number,
  ctx: RabbitStepContext,
  rng: () => number,
): boolean {
  const food = ctx.gardenFood;

  if (state.phase === 'eatingFood') {
    state.timerMs -= dt;
    if (state.timerMs <= 0) {
      // Prefer callback; if ctx dropped mid-eat, still settle cleanly.
      food?.onEatComplete?.();
      state.foodNoticeMs = -1;
      settle(state, ctx, rng);
    }
    return true;
  }

  if (state.phase === 'seekingFood') {
    if (state.hop) {
      advanceHop(state, dt);
      if (state.hop) return true;
      if (food && isNearFood(state, food, ctx.worldAspect)) {
        startEatingFood(state, ctx, rng);
        return true;
      }
      state.timerMs = pickRange(RABBIT_TIMING.settleMs, rng) * 0.35;
      return true;
    }

    state.timerMs -= dt;
    if (state.timerMs > 0) return true;

    if (!food?.available) {
      settle(state, ctx, rng);
      return true;
    }

    if (isNearFood(state, food, ctx.worldAspect)) {
      startEatingFood(state, ctx, rng);
      return true;
    }

    hopTowardFood(state, ctx, rng, food);
    return true;
  }

  if (!food?.available) {
    state.foodNoticeMs = -1;
    return false;
  }

  if (
    state.phase === 'startled' ||
    state.phase === 'leaving' ||
    state.phase === 'away'
  ) {
    return false;
  }

  if (state.foodNoticeMs < 0) {
    if (isCalmForFood(state)) {
      state.foodNoticeMs = pickRange(RABBIT_FOOD_NOTICE_MS, rng);
    }
    return false;
  }

  if (state.foodNoticeMs > 0) {
    state.foodNoticeMs -= dt;
    if (state.foodNoticeMs <= 0 && isCalmForFood(state)) {
      beginSeekingFood(state, food);
    }
    return false;
  }

  return false;
}

function isNearFood(
  state: RabbitState,
  food: SpawnPoint,
  worldAspect: number,
): boolean {
  return rabbitDistance(state, food, worldAspect) < RABBIT_FOOD_REACHED;
}

function isCalmForFood(state: RabbitState): boolean {
  return (
    state.phase === 'settled' ||
    (state.phase === 'hopping' && !state.fleeQueued && !state.hop)
  );
}

function beginSeekingFood(state: RabbitState, food: GardenFoodTarget): void {
  food.onTargeted?.();
  state.phase = 'seekingFood';
  state.pose = 'hop';
  state.hop = null;
  state.liftT = 0;
  state.fleeQueued = false;
  state.foodNoticeMs = 0;
  state.timerMs = 0;
}

function startEatingFood(
  state: RabbitState,
  ctx: RabbitStepContext,
  rng: () => number,
): void {
  ctx.gardenFood?.onBeginEat?.();
  state.phase = 'eatingFood';
  state.pose = 'sniff';
  state.hop = null;
  state.liftT = 0;
  state.timerMs = pickRange(RABBIT_FOOD_EAT_MS, rng);
}

function hopTowardFood(
  state: RabbitState,
  ctx: RabbitStepContext,
  rng: () => number,
  food: GardenFoodTarget,
): void {
  const aspect = ctx.worldAspect > 0 ? ctx.worldAspect : 1;
  const dx = food.x - state.x;
  const dy = (food.y - state.y) / aspect;
  const dist = Math.hypot(dx, dy);
  if (dist < 0.0005) return;

  const reach = Math.min(
    dist * 0.9,
    RABBIT_HOP_DISTANCE.max,
  );
  const ux = dx / dist;
  const uy = dy / dist;
  const candidate = {
    x: state.x + ux * reach,
    y: state.y + uy * reach * aspect,
  };
  const target = isLegalSpot(candidate, ctx, RABBIT_PLAYER_CLEARANCE)
    ? candidate
    : pickRoamTarget(state, ctx, rng, RABBIT_HOP_DISTANCE) ?? candidate;

  state.phase = 'seekingFood';
  state.pose = 'hop';
  beginHop(state, target, pickRange(RABBIT_TIMING.hopMs, rng), 1);
}
